import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, Send, Users, CheckCircle2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { APP_VERSION, BUILD_DATE } from '../version';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralEmail, setReferralEmail] = useState('');
  const [sendingReferral, setSendingReferral] = useState(false);
  const [referralSent, setReferralSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReferral = async (e) => {
    e.preventDefault();
    if (!referralEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingReferral(true);
    try {
      await api.post('/referral/send', { email: referralEmail });
      setReferralSent(true);
      setReferralEmail('');
      toast.success('Referral sent! They\'ll receive an invitation email.');
      setTimeout(() => setReferralSent(false), 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send referral');
    } finally {
      setSendingReferral(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-background bg-grid flex">
      {/* Left side - Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative brand-gradient overflow-hidden">
        {/* mesh glows */}
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/20">
              <Briefcase className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold tracking-tight font-heading">My Project Agent</span>
          </div>

          <div className="max-w-md">
            <h2 className="text-4xl font-bold leading-tight mb-5 font-heading">Track every PO.<br/>Never miss a booking.</h2>
            <p className="text-white/85 text-lg mb-8">AI-powered job & purchase-order management for contractors working with insurance builders and suppliers.</p>
            <ul className="space-y-3">
              {[
                'Auto-import POs straight from your inbox',
                'Booking dates on a shared calendar',
                'Daily next-day trade & job reports',
                'Automatic supplier update reminders',
              ].map((f) => (
                <li key={f} className="flex items-center gap-3 text-white/95">
                  <span className="h-6 w-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-white/60 text-sm">Built for the field. Powered by AI.</p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          <Card className="surface">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="h-14 w-14 rounded-2xl brand-gradient flex items-center justify-center shadow-glow">
                    <Briefcase className="h-7 w-7 text-white" />
                  </div>
                  <span className="text-4xl font-bold tracking-tight font-heading">
                    My Project Agent
                  </span>
                </div>
              </div>
              <CardDescription>Sign in to your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-input border-border rounded-lg"
                    data-testid="login-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-input border-border rounded-lg pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full font-semibold rounded-lg"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-muted-foreground">
                Access is invite-only. Please contact your administrator for an invite.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Version Footer */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
        <p className="text-xs text-muted-foreground/50">
          {APP_VERSION} • {BUILD_DATE}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
